/**
 * Notifications Context
 * Provides centralized notification state and real-time updates via Supabase Broadcast
 * Handles unread notifications display and mark-all-as-read functionality
 */

import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from './auth-context'
import type { RealtimeChannel } from '@supabase/supabase-js'

/**
 * Notification from the database
 */
export interface Notification {
  id: string
  notification_type: 'sync_failed' | 'automation_failed'
  title: string
  message: string
  created_at: string
  /** FK to execution_logs for automation_failed notifications */
  execution_log_id?: string | null
}

interface NotificationsContextType {
  /** List of unread notifications */
  notifications: Notification[]
  /** Count of unread notifications */
  unreadCount: number
  /** Loading state for initial fetch */
  loading: boolean
  /** Mark all notifications as read */
  markAllAsRead: () => Promise<void>
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined)

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)
  
  // Track active channel for proper cleanup
  const channelRef = useRef<RealtimeChannel | null>(null)

  // Fetch unread notifications
  const fetchNotifications = useCallback(async () => {
    if (!user?.id) {
      setNotifications([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)

      const { data, error } = await supabase
        .from('notifications')
        .select('id, notification_type, title, message, created_at, execution_log_id')
        .eq('is_read', false)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('[Notifications] Error fetching notifications:', error)
        return
      }

      setNotifications(data || [])
    } catch (error) {
      console.error('[Notifications] Error fetching notifications:', error)
      setNotifications([])
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  // Subscribe to real-time notifications
  useEffect(() => {
    if (!user?.id) {
      setNotifications([])
      setLoading(false)
      return
    }

    // Fetch initial notifications
    fetchNotifications()

    // Subscribe to broadcast channel for this user
    const channelName = `notifications:${user.id}`
    
    // Set auth for private channel (required for private broadcasts)
    supabase.realtime.setAuth().catch(console.error)
    
    const channel = supabase.channel(channelName, {
      config: { private: true }
    })

    channel
      .on('broadcast', { event: 'new_notification' }, (payload) => {
        console.log('[Notifications] New notification received:', payload)
        
        // Extract notification data from payload
        const newNotification = payload.payload as Notification
        
        if (newNotification && newNotification.id) {
          // Prepend new notification to the list
          setNotifications(prev => [newNotification, ...prev])
        }
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('[Notifications] Connected to channel:', channelName)
        }
      })

    channelRef.current = channel

    // Cleanup on unmount or user change
    return () => {
      if (channelRef.current) {
        console.log('[Notifications] Unsubscribing from channel:', channelName)
        channelRef.current.unsubscribe()
        channelRef.current = null
      }
    }
  }, [user?.id, fetchNotifications])

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!user?.id || notifications.length === 0) return

    try {
      // Optimistically clear local state for instant UI feedback
      setNotifications([])

      // Update database
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false)

      if (error) {
        console.error('[Notifications] Error marking as read:', error)
        // Refetch to restore state on error
        await fetchNotifications()
      }
    } catch (error) {
      console.error('[Notifications] Error marking as read:', error)
      // Refetch to restore state on error
      await fetchNotifications()
    }
  }, [user?.id, notifications.length, fetchNotifications])

  const value = useMemo(() => ({
    notifications,
    unreadCount: notifications.length,
    loading,
    markAllAsRead,
  }), [notifications, loading, markAllAsRead])

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  )
}

export function useNotifications() {
  const context = useContext(NotificationsContext)
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationsProvider')
  }
  return context
}

